import type { ExpenseDraft, ExpenseMutationIds, ExpensePlan, FinancialSnapshot, Movement, WeekId } from "./types";
import { splitPeriodWeekRanges } from "./periods";
import { firstInstallmentDraft, firstInstallmentSlice, installmentSchedule, isInstallmentDraft } from "./installments";

const TRACKED_CARD_METHODS = new Set([
  "BBVA",
  "American Express",
  "American Express Platinum Card",
  "AMEX Servicios",
  "Scotiabank",
]);

export function isTrackedCard(paymentMethod: string): boolean {
  return TRACKED_CARD_METHODS.has(paymentMethod);
}

/** Métodos que retiran dinero de Nu en el momento de capturar la compra. */
export function isImmediateNuPayment(paymentMethod: string): boolean {
  return paymentMethod === "Transferencia" || paymentMethod === "Débito";
}

export function equalShareReimbursementCents(amountCents: number): number {
  if (!Number.isFinite(amountCents) || amountCents <= 0) return 0;
  return Math.ceil(amountCents / 200) * 100;
}

function isoDate(year: number, month: number, day: number): string {
  return [year, String(month + 1).padStart(2, "0"), String(day).padStart(2, "0")].join("-");
}

function cutDate(year: number, month: number, cutDay: number): Date {
  const lastDay = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(Math.max(1, cutDay), lastDay), 12);
}

/** Replica el corte original: el periodo inicia al día siguiente del corte. */
export function cardPeriodForDate(spentOn: string, cutDay: number): string | undefined {
  const match = spentOn.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match || !Number.isInteger(cutDay) || cutDay < 1 || cutDay > 31) return undefined;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);
  if (Number.isNaN(date.getTime())) return undefined;
  const end = date.getDate() > cutDay
    ? cutDate(date.getFullYear(), date.getMonth() + 1, cutDay)
    : cutDate(date.getFullYear(), date.getMonth(), cutDay);
  const previousEnd = cutDate(end.getFullYear(), end.getMonth() - 1, cutDay);
  const start = new Date(previousEnd);
  start.setDate(start.getDate() + 1);
  return `${isoDate(start.getFullYear(), start.getMonth(), start.getDate())} al ${isoDate(end.getFullYear(), end.getMonth(), end.getDate())}`;
}

function cardPeriodBounds(period: string): { start: string; end: string } | null {
  const match = period.match(/^(\d{4}-\d{2}-\d{2})\s+al\s+(\d{4}-\d{2}-\d{2})$/i);
  return match ? { start: match[1], end: match[2] } : null;
}

/** Usa un periodo ya conocido cuando la tarjeta todavía no tiene corte configurado. */
export function knownCardPeriodForExpense(
  snapshot: FinancialSnapshot,
  paymentMethod: string,
  spentOn?: string,
): string | undefined {
  const date = String(spentOn ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return undefined;
  const periods = snapshot.movements
    .filter((movement) => movement.kind === "expense" && movement.paymentMethod === paymentMethod)
    .map((movement) => movement.cardPeriod)
    .filter((period): period is string => Boolean(period));
  return periods.find((period) => {
    const bounds = cardPeriodBounds(period);
    return Boolean(bounds && date >= bounds.start && date <= bounds.end);
  });
}

export function ownFundsUsedToday(draft: ExpenseDraft): number {
  const firstInstallment = firstInstallmentSlice(draft);
  const reimbursementCents = Math.max(0, firstInstallment?.reimbursementCents ?? draft.reimbursementCents ?? 0);
  const realExpenseCents = Math.max(0, (firstInstallment?.billedCents ?? draft.amountCents) - reimbursementCents);
  if (!isTrackedCard(draft.paymentMethod)) return realExpenseCents;
  if (draft.cardFundingMode === "later") return 0;
  if (draft.cardFundingMode === "partial") {
    return Math.min(realExpenseCents, Math.max(0, draft.amountTodayCents ?? 0));
  }
  return realExpenseCents;
}

/** La fecha decide a qué semana de la quincena pertenece una compra. */
export function expenseWeekIdForDate(snapshot: FinancialSnapshot, spentOn: string): WeekId | undefined {
  const match = spentOn.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const startMatch = snapshot.periodStart?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const endMatch = snapshot.periodEnd?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match || !startMatch || !endMatch) return undefined;
  const parse = (parts: RegExpMatchArray) => new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]), 12);
  const spent = parse(match);
  const start = parse(startMatch);
  const end = parse(endMatch);
  const ranges = splitPeriodWeekRanges(snapshot.periodStart, snapshot.periodEnd);
  if (Number.isNaN(spent.getTime())
    || spent.getFullYear() !== Number(match[1])
    || spent.getMonth() !== Number(match[2]) - 1
    || spent.getDate() !== Number(match[3])
    || spent < start
    || spent > end
    || !ranges) return undefined;
  return spentOn <= ranges.week1.end ? "week1" : "week2";
}

/**
 * Reconoce la quincena vigente aunque una migración haya guardado su nombre
 * como slug (`agosto-q2-2026`) y la fotografía use la etiqueta visible
 * (`Agosto Q2-2026`). La fecha del gasto es la fuente más precisa cuando está
 * disponible, porque además evita confundir periodos de años distintos.
 */
export function isMovementInCurrentPeriod(
  snapshot: Pick<FinancialSnapshot, "periodLabel" | "periodStart" | "periodEnd">,
  movement: Pick<Movement, "periodId" | "spentOn" | "accountingPeriodId">,
): boolean {
  if (movement.accountingPeriodId) return isMovementInCurrentPeriod(snapshot, { periodId: movement.accountingPeriodId });
  const spentOn = String(movement.spentOn ?? "").slice(0, 10);
  const start = String(snapshot.periodStart ?? "");
  const end = String(snapshot.periodEnd ?? "");
  if (/^\d{4}-\d{2}-\d{2}$/.test(spentOn) && /^\d{4}-\d{2}-\d{2}$/.test(start) && /^\d{4}-\d{2}-\d{2}$/.test(end)) {
    return spentOn >= start && spentOn <= end;
  }
  const key = (value: string | undefined) => String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es-MX")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const movementPeriod = key(movement.periodId);
  return Boolean(movementPeriod && movementPeriod === key(snapshot.periodLabel));
}

export function planExpense(snapshot: FinancialSnapshot, draft: ExpenseDraft): ExpensePlan {
  if (!Number.isInteger(draft.amountCents) || draft.amountCents <= 0) {
    return { allowed: false, reason: "invalid_amount" };
  }

  const reimbursementCents = draft.reimbursementCents ?? 0;
  if (!Number.isInteger(reimbursementCents) || reimbursementCents < 0 || reimbursementCents > draft.amountCents) {
    return { allowed: false, reason: "invalid_reimbursement" };
  }
  const requestedSavingsCents = draft.savingsContributionCents ?? 0;
  if (!Number.isInteger(requestedSavingsCents) || requestedSavingsCents < 0) {
    return { allowed: false, reason: "invalid_savings" };
  }
  const installmentRequested = draft.installmentCount !== undefined;
  const installments = installmentSchedule(draft);
  if (installmentRequested && (
    !isTrackedCard(draft.paymentMethod)
    || !Number.isInteger(draft.cardCutDay)
    || Number(draft.cardCutDay) < 1
    || Number(draft.cardCutDay) > 31
    || installments.length !== draft.installmentCount
  )) {
    return { allowed: false, reason: "invalid_installments" };
  }
  if (installmentRequested && requestedSavingsCents > 0) {
    return { allowed: false, reason: "invalid_savings" };
  }
  const firstInstallment = installments[0];
  const realExpenseCents = firstInstallment?.ownCents ?? draft.amountCents - reimbursementCents;
  if (isTrackedCard(draft.paymentMethod) && draft.cardFundingMode === "partial") {
    const partialCents = draft.amountTodayCents ?? 0;
    if (!Number.isInteger(partialCents) || partialCents <= 0 || partialCents > realExpenseCents) {
      return { allowed: false, reason: "invalid_card_funding" };
    }
  }

  const week = snapshot.weeks.find((candidate) => candidate.id === draft.weekId);
  if (!week) return { allowed: false, reason: "week_not_found" };

  // La fotografía `current` de Firebase sigue siendo la autoridad hasta que la
  // siguiente quincena se activa transaccionalmente. Esto permite completar el
  // periodo que acaba de terminar sin reasignar el gasto a una semana nueva.
  // Una semana futura continúa bloqueada y una semana cerrada exige su fecha.
  if (week.status === "upcoming") return { allowed: false, reason: "week_not_active" };

  if (draft.spentOn && snapshot.periodStart && snapshot.periodEnd) {
    const match = draft.spentOn.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const start = new Date(`${snapshot.periodStart}T12:00:00`);
    const end = new Date(`${snapshot.periodEnd}T12:00:00`);
    const spent = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12) : new Date(Number.NaN);
    const validCalendarDate = Boolean(match)
      && spent.getFullYear() === Number(match?.[1])
      && spent.getMonth() === Number(match?.[2]) - 1
      && spent.getDate() === Number(match?.[3]);
    const derivedWeekId = expenseWeekIdForDate(snapshot, draft.spentOn);
    if (!validCalendarDate || spent < start || spent > end || derivedWeekId !== draft.weekId) {
      return { allowed: false, reason: "invalid_date" };
    }
  } else if (week.status === "closed") {
    return { allowed: false, reason: "invalid_date" };
  }

  // El formulario original sólo permite ahorro por compra cuando el gasto sí
  // sale del presupuesto semanal. Se normaliza también aquí para proteger la
  // transacción de Firebase ante borradores antiguos o manipulados.
  const savingsContributionCents = draft.budgetImpact === false
    ? 0
    : requestedSavingsCents;
  const ownTodayCents = ownFundsUsedToday(draft);
  const weeklyImpactCents = draft.budgetImpact === false ? 0 : ownTodayCents + savingsContributionCents;
  // Sólo el apartado base "Disponible" puede rescatar una semana. Los apartados
  // con nombre quedan fuera aunque el usuario no los haya marcado como protegidos.
  const allocatedSavingsCents = Math.max(
    snapshot.protectedSavingsCents,
    snapshot.allocatedSavingsCents ?? snapshot.protectedSavingsCents,
  );
  const availableSavings = Math.max(0, snapshot.savingsCents - allocatedSavingsCents);
  const savingsRescueCents = Math.max(0, weeklyImpactCents - week.availableCents);

  const accountDeltaCents = (draft.reimbursementReceived ? reimbursementCents : 0)
    - (isImmediateNuPayment(draft.paymentMethod) ? draft.amountCents : 0);
  const accountBalanceCents = snapshot.accounts[0]?.balanceCents ?? 0;
  if (accountBalanceCents + accountDeltaCents < 0) {
    return {
      allowed: false,
      reason: "insufficient_account",
      missingCents: -(accountBalanceCents + accountDeltaCents),
    };
  }

  if (savingsRescueCents > availableSavings) {
    return {
      allowed: false,
      reason: "insufficient_total",
      missingCents: savingsRescueCents - availableSavings,
    };
  }

  return {
    allowed: true,
    weeklyImpactCents,
    savingsRescueCents,
    remainingWeekCents: Math.max(0, week.availableCents - weeklyImpactCents),
    remainingSavingsCents: snapshot.savingsCents + savingsContributionCents - savingsRescueCents,
  };
}

export function applyExpense(
  snapshot: FinancialSnapshot,
  draft: ExpenseDraft,
  plan: Extract<ExpensePlan, { allowed: true }>,
  now = new Date(),
  ids?: ExpenseMutationIds,
): FinancialSnapshot {
  const expenseId = ids?.expenseId ?? `GASTO_${crypto.randomUUID()}`;
  const periodId = snapshot.periodLabel ?? snapshot.movements.find((movement) => movement.periodId)?.periodId ?? "periodo-actual";
  if (isInstallmentDraft(draft)) {
    const first = firstInstallmentSlice(draft);
    const firstDraft = firstInstallmentDraft(draft);
    if (!first || !firstDraft) throw new Error("El plan de parcialidades no es válido.");
    const applied = applyExpense(snapshot, firstDraft, plan, now, {
      expenseId,
      savingsMovementId: ids?.savingsMovementId,
      rescueMovementId: ids?.rescueMovementId,
    });
    const additionalReceivedCents = draft.reimbursementReceived
      ? Math.max(0, (draft.reimbursementCents ?? 0) - first.reimbursementCents)
      : 0;
    const firstMovement = applied.movements.find((movement) => movement.id === expenseId);
    if (!firstMovement) throw new Error("No se pudo crear la primera parcialidad.");
    const nextMovement: Movement = {
      ...firstMovement,
      accountDeltaCents: (firstMovement.accountDeltaCents ?? 0) + additionalReceivedCents,
      installmentPurchaseId: expenseId,
      installmentNumber: 1,
      installmentCount: first.count,
      installmentTotalCents: draft.amountCents,
      installmentTotalReimbursementCents: draft.reimbursementCents ?? 0,
      installmentDueOn: first.dueOn,
      installmentPurchaseDate: draft.spentOn,
      installmentPurchasePeriodId: periodId,
      installmentRecognizedCents: first.ownCents,
    };
    let reserveFound = false;
    const cardPayments = applied.cardPayments.map((card) => {
      if (card.name !== draft.paymentMethod) return card;
      reserveFound = true;
      return { ...card, amountCents: card.amountCents + additionalReceivedCents };
    });
    if (!reserveFound && additionalReceivedCents > 0) {
      cardPayments.push({ name: draft.paymentMethod, amountCents: additionalReceivedCents });
    }
    return {
      ...applied,
      accounts: applied.accounts.map((account, index) => index === 0
        ? { ...account, balanceCents: account.balanceCents + additionalReceivedCents }
        : account),
      cardPayments,
      movements: applied.movements.map((movement) => movement.id === expenseId ? nextMovement : movement),
    };
  }
  const reimbursementCents = Math.max(0, draft.reimbursementCents ?? 0);
  const savingsContributionCents = draft.budgetImpact === false
    ? 0
    : Math.max(0, draft.savingsContributionCents ?? 0);
  const realExpenseCents = Math.max(0, draft.amountCents - reimbursementCents);
  const ownTodayCents = ownFundsUsedToday(draft);
  const weeklyCategoryCents = draft.budgetImpact === false ? 0 : ownTodayCents;
  const reimbursementCoveredCents = draft.reimbursementReceived ? reimbursementCents : 0;
  const coveredAmountCents = Math.min(
    draft.amountCents,
    isTrackedCard(draft.paymentMethod)
      ? ownTodayCents + reimbursementCoveredCents
      : realExpenseCents + reimbursementCoveredCents,
  );
  const pendingAmountCents = Math.max(0, draft.amountCents - coveredAmountCents);
  const accountDeltaCents = (draft.reimbursementReceived ? reimbursementCents : 0)
    - (isImmediateNuPayment(draft.paymentMethod) ? draft.amountCents : 0);
  let cardPaymentFound = false;
  const cardPayments = snapshot.cardPayments.map((card) => {
    if (!isTrackedCard(draft.paymentMethod) || card.name !== draft.paymentMethod) return card;
    cardPaymentFound = true;
    return { ...card, amountCents: card.amountCents + coveredAmountCents };
  });
  if (isTrackedCard(draft.paymentMethod) && !cardPaymentFound && coveredAmountCents > 0) {
    cardPayments.push({ name: draft.paymentMethod, amountCents: coveredAmountCents });
  }
  const movements: Movement[] = [
    {
      id: expenseId,
      periodId,
      kind: "expense",
      amountCents: realExpenseCents,
      billedAmountCents: draft.amountCents,
      merchant: draft.merchant || "Sin comercio",
      description: draft.merchant || "Sin comercio",
      paymentMethod: draft.paymentMethod,
      weekId: draft.weekId,
      category: draft.category,
      reimbursementCents,
      reimbursementReceived: draft.reimbursementReceived,
      savingsContributionCents,
      savingsRescueCents: plan.savingsRescueCents,
      budgetImpact: draft.budgetImpact,
      cardFundingMode: draft.cardFundingMode,
      amountTodayCents: draft.amountTodayCents,
      promiseDate: draft.promiseDate,
      weeklyImpactCents: plan.weeklyImpactCents,
      accountDeltaCents,
      paidNow: pendingAmountCents === 0,
      coveredAmountCents,
      pendingAmountCents,
      cardStatus: pendingAmountCents > 0 ? "Pendiente" : "Cubierto",
      spentOn: draft.spentOn,
      cardPeriod: draft.cardPeriod ?? knownCardPeriodForExpense(snapshot, draft.paymentMethod, draft.spentOn),
      sharedExpense: draft.sharedExpense,
      receiptAttachmentId: draft.receiptName ? expenseId : undefined,
      receiptName: draft.receiptName,
      location: draft.location,
      origin: "app",
      schemaVersion: 3,
      createdAt: now.toISOString(),
      syncState: "pending",
    },
  ];

  if (savingsContributionCents > 0) {
    movements.push({
      id: ids?.savingsMovementId ?? `AHORRO_COMPRA_${expenseId}`,
      periodId,
      kind: "savings_contribution",
      amountCents: savingsContributionCents,
      merchant: "Ahorro por compras",
      description: `Ahorro vinculado a ${draft.merchant || "gasto"}`,
      paymentMethod: "Semana actual",
      weekId: draft.weekId,
      createdAt: now.toISOString(),
      spentOn: draft.spentOn,
      syncState: "pending",
      relatedMovementId: expenseId,
      savingsBucketId: "DISPONIBLE",
      savingsSource: "Semana actual",
      origin: "app",
      schemaVersion: 3,
    });
  }

  if (plan.savingsRescueCents > 0) {
    movements.push({
      id: ids?.rescueMovementId ?? `RESCATE_GASTO_${expenseId}`,
      periodId,
      kind: "savings_rescue",
      amountCents: plan.savingsRescueCents,
      merchant: `Rescate para ${draft.merchant || "gasto"}`,
      paymentMethod: draft.paymentMethod,
      weekId: draft.weekId,
      createdAt: now.toISOString(),
      syncState: "pending",
      relatedMovementId: expenseId,
      origin: "app",
      schemaVersion: 3,
    });
  }

  return {
    ...snapshot,
    updatedAt: now.toISOString(),
    source: "local",
    accounts: snapshot.accounts.map((account, index) => index === 0
      ? { ...account, balanceCents: account.balanceCents + accountDeltaCents }
      : account),
    cardPayments,
    savingsCents: plan.remainingSavingsCents,
    savingsThisPeriodCents: snapshot.savingsThisPeriodCents + savingsContributionCents,
    totalSpentCents: snapshot.totalSpentCents + realExpenseCents,
    weeks: snapshot.weeks.map((week) => {
      if (week.id !== draft.weekId) return week;
      const categorySpend = weeklyCategoryCents > 0 && draft.category
        ? (week.categorySpend ?? [])
          .map((category) => category.name === draft.category ? { ...category, amountCents: category.amountCents + weeklyCategoryCents } : category)
          .concat((week.categorySpend ?? []).some((category) => category.name === draft.category)
            ? []
            : [{ name: draft.category, amountCents: weeklyCategoryCents, icon: "✦" }])
          .sort((a, b) => b.amountCents - a.amountCents)
        : week.categorySpend;
      return {
        ...week,
        availableCents: plan.remainingWeekCents,
        spentCents: week.spentCents + plan.weeklyImpactCents,
        categorySpend,
        savingsAppliedCents: (week.savingsAppliedCents ?? 0) + savingsContributionCents,
      };
    }),
    categorySpend: snapshot.categorySpend
      .map((category) => category.name === draft.category
        ? { ...category, amountCents: category.amountCents + realExpenseCents }
        : category)
      .concat(
        draft.category && !snapshot.categorySpend.some((category) => category.name === draft.category)
          ? [{ name: draft.category, amountCents: realExpenseCents, icon: "✦" }]
          : [],
      )
      .sort((a, b) => b.amountCents - a.amountCents),
    movements: [...movements, ...snapshot.movements].slice(0, 200),
  };
}

export function parseMoneyToCents(value: string): number {
  const normalized = value.replace(/[^\d.,-]/g, "").replace(/,/g, "");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

export function formatMoney(cents: number): string {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
  }).format(cents / 100);
}
